/**
 * What the security check can honestly report.
 *
 * The wording is carried over deliberately. This panel exists to say what a
 * client-side gate is and is not, on the same screen that asks for a password —
 * so nobody mistakes the sign-in for protection of the data behind it.
 */
import { accounts, currentSession, GATE } from "./accounts";
import { cryptoReady } from "./crypto";
import { t } from "../../i18n";

export type State = "go" | "caution" | "nogo" | "idle";

export interface CheckItem {
  k: string;
  label: string;
  state: State;
  value: string;
  note: string;
}

export interface CheckResult {
  items: CheckItem[];
  bad: number;
  warn: number;
  score: number;
}

export function securityCheck(devOpen: boolean, auditCount: number | null): CheckResult {
  const https = location.protocol === "https:";
  const file = location.protocol === "file:";
  const list = accounts();
  const ses = currentSession();
  const out: CheckItem[] = [];

  if (devOpen)
    out.push({
      k: "dev",
      label: t("Development mode"),
      state: "nogo",
      value: t("sign-in disabled — anyone can enter"),
      note: t(
        "DEV_OPEN is true, so the credentials and the authenticator are switched off while the pages are built. Set it to false and rebuild before this reaches anybody.",
      ),
    });

  out.push(
    {
      k: "gate",
      label: t("Sign-in gate"),
      state: list.length ? "caution" : "nogo",
      value: list.length ? t("{n} account(s) held on this device", { n: list.length }) : t("no account set up"),
      note: t(
        "The gate runs inside the file it protects. It stops someone using the screen; it does not stop anyone reading the file.",
      ),
    },
    {
      k: "hash",
      label: t("Password storage"),
      state: cryptoReady() ? "go" : "nogo",
      value: cryptoReady()
        ? t("PBKDF2-SHA256, 150,000 iterations, per-account salt")
        : t("unavailable — this browser has no Web Crypto"),
      note: t(
        "Real hashing, so a stolen account file does not hand over the password. It does not protect the data, which is not encrypted.",
      ),
    },
    {
      k: "mfa",
      label: t("Second factor"),
      state: list.some((a) => a.secret) ? "go" : "caution",
      value: list.some((a) => a.secret) ? t("RFC 6238 TOTP, works with any authenticator app") : t("not enrolled"),
      note: t("Genuine time-based codes, verified offline against the enrolled secret."),
    },
    {
      k: "transport",
      label: t("How the file is being served"),
      state: https ? "go" : file ? "nogo" : "caution",
      value: https ? "HTTPS" : file ? t("opened directly from disk (file://)") : location.protocol,
      note: file
        ? t("A file on disk can be copied, mailed or opened by anyone with the machine.")
        : https
          ? t("Transport is encrypted. The contents of the file still are not.")
          : t("Not served over HTTPS — anything sent to a connected model or mail service is exposed."),
    },
    {
      k: "data",
      label: t("Data at rest"),
      state: "nogo",
      value: t("embedded in the page, unencrypted"),
      note: t(
        "The aerodrome drawing, floor plan, friction survey and every register are inside this file. Anyone holding it holds all of it, signed in or not.",
      ),
    },
    {
      k: "source",
      label: t("Source visibility"),
      state: "nogo",
      value: t("readable — view source shows every line"),
      note: t("Including this check. Client-side security is a courtesy, not a control."),
    },
    {
      k: "session",
      label: t("Session"),
      state: ses ? "go" : "caution",
      value: ses
        ? t("{email} · idles out after {n} minutes", { email: ses.email, n: GATE.idleMin })
        : t("not signed in"),
      note: t("Held in session storage, so closing the tab ends it."),
    },
    {
      k: "lockout",
      label: t("Brute force"),
      state: "go",
      value: t("{tries} attempts, then {n} seconds locked", { tries: GATE.maxTries, n: GATE.lockSec }),
      note: t("Slows guessing at the screen. Anyone editing the file bypasses it entirely."),
    },
    {
      k: "audit",
      label: t("Audit trail"),
      state: auditCount != null ? "caution" : "nogo",
      value: auditCount != null ? t("{n} entry(s) this session", { n: auditCount }) : t("none"),
      note: t("Held in memory and lost on reload. It is a working record, not an auditable log."),
    },
    {
      k: "backend",
      label: t("Server-side enforcement"),
      state: "nogo",
      value: t("none — there is no server"),
      note: t(
        "This is the single fix that changes everything: serve the app, keep the data behind an API, and let accounts decide what each person can see.",
      ),
    },
  );

  const bad = out.filter((x) => x.state === "nogo").length;
  const warn = out.filter((x) => x.state === "caution").length;
  return {
    items: out,
    bad,
    warn,
    score: Math.round(((out.length - bad - warn * 0.5) / out.length) * 100),
  };
}
